import { Link } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";
import { IMG } from "@/lib/images";
import { Chapter, Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import SectionCTA from "@/components/SectionCTA";
import { WHY_ITEMS, usePageMeta } from "@/lib/site";

const LEADERS = [
  {
    name: "Sini Tauauvea",
    role: "General Manager",
    bio: "Sini leads Black Sky Builders' business development, client relationships, estimating opportunities, company strategy, partnerships, project development, and overall company direction.",
    testid: "about-leader-sini-card",
  },
  {
    name: "Dusty Casey",
    role: "Operations Manager",
    bio: "Dusty leads construction operations, project planning, field coordination, scheduling, quality control, subcontractor relationships, constructability, and project execution.",
    testid: "about-leader-dusty-card",
  },
];

export default function About() {
  usePageMeta(
    "About Black Sky Builders — Salt Lake City Construction",
    "Black Sky Builders is a Salt Lake City construction company built on experienced leadership, strong relationships, accountability, and projects that improve how a property functions."
  );

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        lines={["Builders Who", "Know The Work"]}
        copy="Black Sky Builders was built around experienced construction leadership, strong relationships, accountability, and the belief that the right construction project should improve both the way a property functions and the value it provides its owner."
        image={IMG.mountains}
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Chapter
              index="01"
              eyebrow="The Company"
              title="Field experience meets project leadership"
            />
            <div className="-mt-4 space-y-5">
              <Reveal>
                <p className="text-sm leading-relaxed text-mist sm:text-base">
                  Black Sky Builders combines field experience, project leadership, planning, subcontractor
                  relationships, and business management to help clients move projects from concept through
                  completion. We understand construction. We understand project costs. We understand coordination.
                  And we understand that communication matters just as much as the finished product.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-sm leading-relaxed text-mist sm:text-base">
                  Based in Salt Lake City, we serve homeowners, property owners, and businesses across Utah —
                  from additions and garage apartments to commercial improvements and concrete.
                </p>
              </Reveal>
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="border border-line bg-coal p-3">
              <img src={IMG.tradesperson} alt="Builder reviewing plans in the field" loading="lazy" className="h-[340px] w-full object-cover grayscale sm:h-[420px]" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-line bg-coal">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16" data-testid="about-leadership">
          <Chapter
            index="02"
            eyebrow="Leadership"
            title="The team behind the work"
            copy="Together, the leadership team combines business development, project management, field experience, and an established network of construction professionals to guide projects from early planning through completion."
          />
          <div className="grid gap-5 md:grid-cols-2">
            {LEADERS.map((leader, i) => (
              <Reveal key={leader.name} delay={i * 0.1} className="h-full">
                <div data-testid={leader.testid} className="flex h-full flex-col border border-line bg-ink">
                  <div className="flex h-56 flex-col items-center justify-center border-b border-dashed border-steel/60 bg-coal">
                    <Camera className="h-6 w-6 text-steel" />
                    <span className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-steel">
                      Professional headshot coming soon
                    </span>
                  </div>
                  <div className="p-6 sm:p-8">
                    <h3 className="font-display text-xl font-bold uppercase tracking-tight">{leader.name}</h3>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-fog">{leader.role}</p>
                    <p className="mt-4 text-sm leading-relaxed text-fog">{leader.bio}</p>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
                      Full bio coming soon
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16" data-testid="about-values">
        <Chapter
          index="03"
          eyebrow="How We Work"
          title="Standards, not slogans"
          copy="What you can expect when Black Sky Builders is responsible for your project."
        />
        <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
          {WHY_ITEMS.map((item, i) => (
            <Reveal key={item.t} delay={(i % 3) * 0.06} className="h-full">
              <div className="h-full bg-ink p-6">
                <span className="font-mono text-xs tracking-[0.3em] text-steel">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-base font-bold uppercase tracking-wide">{item.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fog">{item.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <Link to="/contact" data-testid="about-cta-button" className="btn-primary">
            Request a Project Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      <SectionCTA />
    </div>
  );
}
